import { motion, useReducedMotion } from "framer-motion";
import { MASCOT } from "@/lib/mascot";
import { cn as cx } from "@/lib/utils";

/**
 * Full-scene composition for the LessonPlayer:
 *  - Chalky blackboard background with the slide title in "chalk" text
 *  - Wooden classroom floor + soft rug hint
 *  - Prof. Scaly standing to the left with idle / talking animations
 *  - Speech bubble on the right showing the current sentence
 *
 * Props:
 *   title      — bilingual { cn, en }
 *   caption    — the sentence Prof. Scaly is currently "speaking" (string)
 *   language   — 'en' | 'cn'
 *   playing    — whether audio is currently playing (drives 'talking' animation)
 */
export function ProfScalyStage({ title, caption, language = "en", playing = false }) {
  const shouldReduce = useReducedMotion();

  // Idle sway — always on (unless reduced motion)
  const idleSway = shouldReduce
    ? {}
    : { rotate: [-1.2, 1.2, -1.2], transition: { duration: 6, repeat: Infinity, ease: "easeInOut" } };

  // Talking bob — extra jitter when narrating
  const talkingAnim = shouldReduce
    ? {}
    : playing
      ? {
          y: [0, -3, 0, -2, 0],
          scale: [1, 1.02, 1, 1.015, 1],
          transition: { duration: 0.6, repeat: Infinity, ease: "easeInOut" },
        }
      : { y: 0, scale: 1, transition: { duration: 0.4 } };

  return (
    <div className="relative w-full rounded-3xl overflow-hidden bg-gradient-to-b from-amber-50 to-orange-50 min-h-[440px] shadow-inner">
      {/* Wooden floor stripe */}
      <div className="absolute inset-x-0 bottom-0 h-[26%] bg-gradient-to-b from-[#c88656] to-[#8b5a2b]">
        <div className="absolute inset-0 opacity-30 bg-[repeating-linear-gradient(90deg,transparent_0_60px,rgba(0,0,0,0.15)_60px_62px)]" />
      </div>

      {/* Blackboard */}
      <div className="absolute top-[6%] right-[4%] w-[64%] h-[62%] rounded-3xl border-[10px] border-[#8b5a2b] shadow-2xl overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#22503b] via-[#1e4535] to-[#173628]">
          {/* Chalk dust */}
          <div className="absolute inset-0 opacity-30 mix-blend-screen bg-[radial-gradient(circle_at_20%_30%,rgba(255,255,255,0.4),transparent_40%),radial-gradient(circle_at_75%_70%,rgba(255,255,255,0.35),transparent_45%)]" />
          {/* Chalk grid streaks */}
          <div className="absolute inset-0 opacity-15 bg-[repeating-linear-gradient(115deg,transparent_0_18px,rgba(255,255,255,0.4)_18px_19px)]" />
        </div>
        <div className="relative h-full flex flex-col justify-center items-center text-center px-6 sm:px-10">
          <div
            className="cn text-3xl sm:text-4xl font-black text-white tracking-wide"
            style={{ fontFamily: '"Fraunces","Noto Sans SC",serif', textShadow: "0 2px 0 rgba(0,0,0,0.15)" }}
          >
            {title?.cn}
          </div>
          <div
            className="mt-2 text-xl sm:text-2xl font-bold text-amber-100/95"
            style={{ fontFamily: '"Fraunces",serif' }}
          >
            {title?.en}
          </div>
          {/* Chalk underline */}
          <div className="mt-3 w-24 h-1 bg-white/40 rounded-full" />
        </div>
      </div>

      {/* Prof. Scaly */}
      <motion.div
        className="absolute left-[3%] bottom-[8%] w-[34%] max-w-[280px]"
        animate={idleSway}
        style={{ transformOrigin: "50% 90%" }}
      >
        <motion.div animate={talkingAnim} className="relative">
          {/* Soft shadow under feet */}
          <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-[75%] h-4 rounded-full bg-black/30 blur-md" />
          {/* Mascot */}
          <img
            src={MASCOT.img}
            alt={`${MASCOT.cn} · ${MASCOT.en}`}
            data-testid="stage-mascot"
            className="relative w-full aspect-square object-cover object-top rounded-[24%] ring-4 ring-white/80 shadow-2xl bg-white"
          />
          {/* Little speech indicator */}
          {playing && !shouldReduce && (
            <motion.div
              className="absolute top-3 -right-2 flex gap-1"
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              data-testid="stage-talking-indicator"
            >
              {[0, 0.15, 0.3].map((delay, i) => (
                <motion.span
                  key={i}
                  className="w-1.5 h-1.5 rounded-full bg-amber-400"
                  animate={{ y: [0, -3, 0], opacity: [0.6, 1, 0.6] }}
                  transition={{ duration: 0.55, delay, repeat: Infinity }}
                />
              ))}
            </motion.div>
          )}
        </motion.div>
      </motion.div>

      {/* Speech bubble */}
      {caption && (
        <motion.div
          key={caption + language}
          initial={{ opacity: 0, y: 8, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.35 }}
          className="absolute left-[36%] bottom-[10%] max-w-[58%] card-plush p-4 sm:p-5 shadow-lg"
          data-testid="stage-speech-bubble"
        >
          {/* Tail */}
          <span className="absolute -left-2 bottom-6 w-4 h-4 rotate-45 bg-white border-l border-b border-slate-100" />
          <div className={cx(
            "leading-snug",
            language === "cn"
              ? "cn text-slate-900 font-bold text-base sm:text-lg"
              : "text-slate-800 font-semibold text-sm sm:text-base",
          )}>
            {caption}
          </div>
        </motion.div>
      )}
    </div>
  );
}

export default ProfScalyStage;
